import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAppSub } from "@/context/AppSubContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PaymentMethods from "@/components/PaymentMethods";
import { Check, Sparkles, LogOut } from "lucide-react";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

const AppSubscribe = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { refresh } = useAppSub();
  const [plans, setPlans] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [method, setMethod] = useState("card");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/app-subscription/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const confirm = async () => {
    if (!chosen) return;
    const reserved = chosen.price > 0 ? prepareExternal() : null;
    setBusy(true);
    try {
      const { data } = await api.post("/app-subscription/subscribe", {
        plan: chosen.key,
        origin_url: window.location.origin,
      });
      // Paid plans -> redirect to Stripe Checkout
      if (data.checkout_url) {
        if (openExternal(data.checkout_url, reserved) === "tab") {
          toast.info("Le paiement Stripe s'est ouvert dans un nouvel onglet.");
          setBusy(false);
        }
        return;
      }
      cancelExternal(reserved);
      // Free trial -> instant activation
      toast.success(data.message);
      await refresh();
      setChosen(null);
      navigate("/");
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-md mx-auto px-4 py-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center"><Sparkles className="h-6 w-6 text-primary" /></div>
          <h1 className="font-display text-3xl">Choisis ton abonnement</h1>
          <p className="text-sm text-muted-foreground">Débloque l'accès complet à MIHOD PAY.</p>
        </div>

        <div className="space-y-3" data-testid="app-plans">
          {plans.map((p) => (
            <button
              key={p.key}
              onClick={() => setChosen(p)}
              data-testid={`app-plan-${p.key}`}
              className={`w-full text-left rounded-2xl border p-4 transition-colors ${p.highlight ? "border-primary bg-primary/5 shadow-[0_0_22px_-6px_hsla(214,100%,62%,0.5)]" : "border-border bg-card hover:border-primary/50"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{p.emoji}</span>
                  <div>
                    <p className="font-display text-lg leading-tight">{p.label}</p>
                    <p className="text-xs text-muted-foreground">{p.desc}</p>
                  </div>
                </div>
                <div className="text-right">
                  {p.badge && <Badge className="mb-1 bg-primary text-primary-foreground border-0">{p.badge}</Badge>}
                  <p className="font-display text-lg">{p.price === 0 ? "0 €" : `${p.price.toFixed(2)} €`}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <button onClick={() => { logout(); navigate("/auth"); }} className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="app-sub-logout">
          <LogOut className="h-3.5 w-3.5" /> Se déconnecter
        </button>
      </div>

      <Dialog open={!!chosen} onOpenChange={(v) => !v && setChosen(null)}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="app-plan-confirm">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">{chosen?.emoji} {chosen?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Prix</span><span className="font-medium">{chosen?.price === 0 ? "Gratuit" : `${chosen?.price?.toFixed(2)} €`}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Durée</span><span className="font-medium">{chosen?.months} mois</span></div>
            {chosen?.key === "trial" ? (
              <p className="text-xs text-muted-foreground rounded-lg bg-secondary/60 p-2.5">Vous bénéficiez de 2 mois gratuits. À la fin de la période d'essai, aucun prélèvement automatique n'est effectué — vous pourrez choisir une formule payante pour continuer.</p>
            ) : (
              <p className="text-xs text-muted-foreground rounded-lg bg-secondary/60 p-2.5">Paiement unique de {chosen?.price?.toFixed(2)} € pour {chosen?.months} mois d'accès. Le renouvellement vous sera proposé à l'échéance.</p>
            )}
            {chosen?.key !== "trial" && (
              <div className="pt-1">
                <p className="text-xs font-medium mb-2">Moyen de paiement</p>
                <PaymentMethods value={method} onChange={setMethod} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChosen(null)}>Retour aux offres</Button>
            <Button onClick={confirm} disabled={busy} data-testid="app-plan-confirm-button"><Check className="h-4 w-4 mr-1" /> {busy ? "…" : (chosen?.key === "trial" ? "Démarrer l'essai" : "Confirmer")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppSubscribe;
